import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext,
  closestCenter,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import {
  Briefcase,
  PlusCircle,
  Circle,
  CheckCircle,
  XCircle,
  Phone,
  Send,
  Loader2,
  Search,
  Filter,
  Grid3X3,
  List,
  Calendar,
  MapPin,
  Building,
  User,
  Clock,
  Euro,
  Eye,
  Edit,
  MoreVertical,
  TrendingUp,
  AlertCircle,
  FileText,
  Users
} from "lucide-react";
import CreateCommandeModal from "./CreateCommandeModal";
import axios from "../../axios/AxiosInstance";
import { useNavigate } from "react-router-dom";
import { formatJsDate } from "../../utils/formatageDate";
import CommandeDetailsView from "./CommandeDetailView";
import { OrderDetails } from "./OrderDetails";

function DraggableCard({ id, children }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id });

  const style = {
    transform: transform
      ? `translate(${transform.x}px, ${transform.y}px)`
      : undefined,
    cursor: "grab",
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
}

function DroppableColumn({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return <div ref={setNodeRef}>{children}</div>;
}

export default function CommandesPage() {
  const [isNewCommande, setIsNewCommande] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isKamban, setIsKamban] = useState(false);
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [orders, setOrders] = useState(null);
  const [commandesByStatus, setCommandeByStatus] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // Nouveaux états pour le design moderne
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban', 'grid', 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('tous');
  const [showFilters, setShowFilters] = useState(false);

  // Statistiques des commandes
  const [stats, setStats] = useState({
    total: 0,
    enCours: 0,
    terminee: 0,
    annulee: 0
  });

  // Fonction pour calculer les statistiques
  const calculateStats = (ordersList) => {
    const total = ordersList.length;
    const enCours = ordersList.filter(o => o.status === 'en cours').length;
    const terminee = ordersList.filter(o => o.status === 'terminee').length;
    const annulee = ordersList.filter(o => o.status === 'annulée').length;

    setStats({ total, enCours, terminee, annulee });
  };

  const loadCommandes = () => {
    setIsLoading(true);
    axios
      .get("order/freelance")
      .then((res) => {
        const ordersData = res.data.map((order) => ({
          ...order,
          loading: false,
        }));

        const grouped = {
          "en cours": [],
          terminee: [],
          annulée: [],
        };

        ordersData.forEach((c) => {
          if (c.status === "terminee") grouped.terminee.push(c);
          else if (c.status === "annulée") grouped.annulée.push(c);
          else grouped["en cours"].push(c);
        });

        setOrders(ordersData);
        setCommandeByStatus(grouped);
        calculateStats(ordersData);
      })
      .catch((e) => {
        if (e.response?.data?.statusCode === 403) {
          localStorage.clear();
          navigate("/login");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onChangePage = (page) => {
    setIsLoading(true);
    axios
      .get(`order/freelance/paginate/${page - 1}`)
      .then((res) => {
        setOrders(res.data);
        setCurrentPage(page);
        console.log(res.data);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadCommandes();
  }, []);

  const showCommande = (commande) => {
    setIsLoading(true);
    axios
      .get(`/order/${commande._id}`)
      .then((res) => {
        console.log(res.data);
        const newCommande = res.data.order;
        const prefix = commande.postCode.substring(0, 2);
        const filtered = res.data.potentialCandidates.filter((cand) => {
          const id = cand._id;
          const excluded = [
            ...newCommande.contactedCandidates,
            ...newCommande.refusedByAgence,
            ...newCommande.acceptedCandidates,
            ...newCommande.confirmedCandidates,
            ...newCommande.submittedToClient,
            ...newCommande.contractedCandidates,
            ...newCommande.refusedCandidates,
          ];
          return (
            !excluded.some((e) => e._id === id) &&
            cand.locations.some((loc) => loc.startsWith(prefix))
          );
        });

        const contactParts = newCommande.contact.split(" ");
        const phone = contactParts.pop();
        const email = contactParts.pop();
        const name = contactParts.join(" ");
        console.log(filtered);
        setSelectedCommande({
          ...newCommande,
          candidates: filtered,
          contactData: { name, email, phone },
        });
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeId = active.id;
    const toStatus = over.id;

    const fromStatus = Object.keys(commandesByStatus).find((status) =>
      commandesByStatus[status].some((c) => c._id === activeId),
    );

    if (!fromStatus || !toStatus || fromStatus === toStatus) return;

    const movedCommande = commandesByStatus[fromStatus].find(
      (c) => c._id === activeId,
    );

    if (!movedCommande) return;

    const updatedCommande = { ...movedCommande, status: toStatus };

    setCommandeByStatus((prev) => {
      const updated = { ...prev };
      updated[fromStatus] = updated[fromStatus].filter(
        (c) => c._id !== activeId,
      );
      updated[toStatus] = [...(updated[toStatus] || []), updatedCommande];
      return updated;
    });
  };

  // Fonction pour filtrer les commandes
  const filterCommandes = (query, status) => {
    if (!orders) return;

    let filtered = orders;

    if (query) {
      filtered = filtered.filter(order =>
        order.qualification?.toLowerCase().includes(query.toLowerCase()) ||
        order.client?.companyName?.toLowerCase().includes(query.toLowerCase()) ||
        order.town?.toLowerCase().includes(query.toLowerCase())
      );
    }

    if (status !== 'tous') {
      filtered = filtered.filter(order => order.status === status);
    }

    return filtered;
  };

  // Fonction pour rechercher
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Fonction pour filtrer par statut
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "annulée":
        return {
          icon: <XCircle className="h-4 w-4 text-red-500" />,
          colorClass: "bg-red-100 text-red-700 border-red-200",
        };
      case "terminee":
        return {
          icon: <CheckCircle className="h-4 w-4 text-green-600" />,
          colorClass: "bg-green-100 text-green-700 border-green-200",
        };
      case "en cours":
        return {
          icon: <Clock className="h-4 w-4 text-orange-500" />,
          colorClass: "bg-orange-100 text-orange-700 border-orange-200",
        };
      default:
        return {
          icon: <Circle className="h-4 w-4 text-gray-400" />,
          colorClass: "bg-gray-100 text-gray-600 border-gray-200",
        };
    }
  };

  const formatStatutLabel = (status) => {
    switch (status) {
      case "terminee":
        return "Terminée";
      case "annulée":
        return "Annulée";
      case "en cours":
        return "En cours";
      default:
        return status;
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="w-full p-8">
        {/* Modals */}
        {isNewCommande && (
          <CreateCommandeModal
            close={() => setIsNewCommande(false)}
            orders={orders}
            setOrders={setOrders}
            setSelectedCommande={setSelectedCommande}
          />
        )}

        {/* Loading Overlay */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-xl p-6 flex items-center space-x-3"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
              >
                <Loader2 className="animate-spin h-6 w-6 text-blue-600" />
                <span className="text-slate-700">Chargement des commandes...</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {!selectedCommande && (
          <>
            {/* Header Section */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Briefcase className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold text-slate-900">
                      Gestion des Commandes
                    </h1>
                    <p className="text-lg text-slate-600">
                      Suivez vos commandes clients de A à Z
                    </p>
                  </div>
                </div>

                <motion.button
                  onClick={() => setIsNewCommande(true)}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <PlusCircle className="w-5 h-5" />
                  <span>Nouvelle commande</span>
                </motion.button>
              </div>
            </motion.div>

            {/* Statistiques */}
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.div
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
                whileHover={{ y: -2 }}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Total commandes</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
                whileHover={{ y: -2 }}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">En cours</p>
                    <p className="text-3xl font-bold text-orange-600">{stats.enCours}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
                whileHover={{ y: -2 }}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Terminées</p>
                    <p className="text-3xl font-bold text-green-600">{stats.terminee}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
                whileHover={{ y: -2 }}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Annulées</p>
                    <p className="text-3xl font-bold text-red-600">{stats.annulee}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Barre d'outils moderne */}
            <motion.div
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex flex-col lg:flex-row items-center space-y-4 lg:space-y-0 lg:space-x-4">
                {/* Recherche */}
                <div className="relative flex-1 w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <motion.input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Rechercher une commande (poste, client, ville...)"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    whileFocus={{ scale: 1.02 }}
                  />
                </div>

                {/* Filtres */}
                <div className="flex items-center space-x-3">
                  <motion.select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                    className="px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    whileFocus={{ scale: 1.02 }}
                  >
                    <option value="tous">Tous les statuts</option>
                    <option value="en cours">En cours</option>
                    <option value="terminee">Terminées</option>
                    <option value="annulée">Annulées</option>
                  </motion.select>

                  {/* Toggle vue */}
                  <div className="flex items-center bg-slate-100 rounded-lg p-1">
                    <motion.button
                      onClick={() => setViewMode('kanban')}
                      className={`p-2 rounded-md transition-colors duration-200 ${
                        viewMode === 'kanban'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Grid3X3 className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-colors duration-200 ${
                        viewMode === 'list'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <List className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {!selectedCommande && (
          <>

          {viewMode === 'kanban' ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {Object.entries(commandesByStatus).map(
                    ([status, commandes]) => (
                      <DroppableColumn id={status} key={status}>
                        <motion.div
                          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 min-h-[400px]"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                              {getStatusInfo(status).icon}
                              <h2 className="text-xl font-semibold text-slate-900">
                                {formatStatutLabel(status)}
                              </h2>
                            </div>
                            <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">
                              {commandes.length}
                            </span>
                          </div>

                          <div className="space-y-4">
                            <AnimatePresence>
                              {commandes.map((commande, index) => (
                                <motion.div
                                  key={commande._id}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -20 }}
                                  transition={{ duration: 0.3, delay: index * 0.1 }}
                                >
                                  <DraggableCard id={commande._id}>
                                    <motion.div
                                      className="bg-white border border-slate-200 rounded-lg p-4 cursor-pointer group hover:shadow-md transition-all duration-200"
                                      whileHover={{ y: -2 }}
                                      onClick={() => showCommande(commande)}
                                    >
                                      <div className="flex items-start justify-between mb-3">
                                        <h3 className="font-semibold text-slate-900 text-sm group-hover:text-blue-600 transition-colors">
                                          {commande.qualification}
                                        </h3>
                                        <motion.button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            showCommande(commande);
                                          }}
                                          className="p-1 text-slate-400 hover:text-blue-600 rounded"
                                          whileHover={{ scale: 1.1 }}
                                          whileTap={{ scale: 0.9 }}
                                        >
                                          <Eye className="w-4 h-4" />
                                        </motion.button>
                                      </div>

                                      <div className="space-y-2 mb-4">
                                        <div className="flex items-center space-x-2 text-xs text-slate-600">
                                          <Building className="w-3 h-3" />
                                          <span className="truncate">{commande.client?.companyName}</span>
                                        </div>
                                        <div className="flex items-center space-x-2 text-xs text-slate-600">
                                          <MapPin className="w-3 h-3" />
                                          <span>{commande.town}</span>
                                        </div>
                                        <div className="flex items-center space-x-2 text-xs text-slate-600">
                                          <Calendar className="w-3 h-3" />
                                          <span>{formatJsDate(commande.startDate)}</span>
                                        </div>
                                      </div>

                                      <div className="flex items-center justify-between">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusInfo(commande.status).colorClass}`}>
                                          {formatStatutLabel(commande.status)}
                                        </span>
                                        {commande.hourlyRate && (
                                          <div className="flex items-center space-x-1 text-xs text-slate-600">
                                            <Euro className="w-3 h-3" />
                                            <span>{commande.hourlyRate}€/h</span>
                                          </div>
                                        )}
                                      </div>
                                    </motion.div>
                                  </DraggableCard>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </div>

                          {commandes.length === 0 && (
                            <div className="text-center py-8">
                              <div className="text-slate-400 mb-2">
                                <FileText className="w-8 h-8 mx-auto" />
                              </div>
                              <p className="text-sm text-slate-500">Aucune commande</p>
                            </div>
                          )}
                        </motion.div>
                      </DroppableColumn>
                    ),
                  )}
                </div>
              </DndContext>
            </motion.div>
          ) : (
            <motion.div
              className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Commande
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Détails
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <AnimatePresence>
                      {(filterCommandes(searchQuery, statusFilter) || orders || []).map((commande, index) => (
                        <motion.tr
                          key={commande._id}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => showCommande(commande)}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          whileHover={{ backgroundColor: '#f8fafc' }}
                        >
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {commande.qualification}
                              </p>
                              <p className="text-xs text-slate-500">
                                {commande.contractType}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2 text-sm text-slate-900">
                                <Building className="w-3 h-3" />
                                <span className="truncate max-w-48">{commande.client?.companyName}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-slate-600">
                                <User className="w-3 h-3" />
                                <span>{commande.recruiter?.firstname || 'Non assigné'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2 text-sm text-slate-600">
                                <MapPin className="w-3 h-3" />
                                <span>{commande.town}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-slate-600">
                                <Calendar className="w-3 h-3" />
                                <span>{formatJsDate(commande.startDate)}</span>
                              </div>
                              {commande.hourlyRate && (
                                <div className="flex items-center space-x-2 text-xs text-slate-600">
                                  <Euro className="w-3 h-3" />
                                  <span>{commande.hourlyRate}€/h</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusInfo(commande.status).colorClass}`}>
                              {getStatusInfo(commande.status).icon}
                              <span>{formatStatutLabel(commande.status)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <motion.button
                              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                showCommande(commande);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </motion.button>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>

              {/* Pagination moderne */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Page {currentPage}</p>
                  <div className="flex space-x-2">
                    <motion.button
                      disabled={currentPage <= 1}
                      onClick={() => onChangePage(currentPage - 1)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        currentPage <= 1
                          ? 'text-slate-400 cursor-not-allowed'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                      }`}
                      whileHover={currentPage > 1 ? { scale: 1.05 } : {}}
                      whileTap={currentPage > 1 ? { scale: 0.95 } : {}}
                    >
                      Précédent
                    </motion.button>
                    <motion.button
                      onClick={() => onChangePage(currentPage + 1)}
                      className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors duration-200"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Suivant
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Message si aucune commande */}
              {(!orders || orders.length === 0) && !isLoading && (
                <motion.div
                  className="text-center py-20"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-slate-400 mb-4">
                    <Briefcase className="w-16 h-16 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune commande trouvée</h3>
                  <p className="text-slate-600 mb-6">
                    {searchQuery ? 'Aucune commande ne correspond à votre recherche.' : 'Commencez par créer votre première commande.'}
                  </p>
                  {!searchQuery && (
                    <motion.button
                      onClick={() => setIsNewCommande(true)}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors duration-200"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Créer une commande
                    </motion.button>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
          </>
        )}

        {/* Détail de commande */}
        <AnimatePresence>
          {selectedCommande && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <OrderDetails
                commande={selectedCommande}
                onClose={() => setSelectedCommande(null)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
